import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { Button, SelectMenuStandard, TextInputBase } from "@truevault/aquilo";
import { FadeTransition } from "../../../pages/get-compliant/FadeTransition";
import { BaseQuestion, BaseQuestionProps } from "./BaseQuestion";
import { QuestionContainer, QuestionLabel } from "./QuestionContainer";

export const SELECT_QUESTION_OTHER_VALUE = "other";

export type SurveySelectOption = {
  value: string;
  label: string;
};

export type SurveySelectQuestionProps = BaseQuestion & {
  type: "select";
  options: SurveySelectOption[];
  placeholder?: string;
  // When set, an "Other" option is appended and choosing it asks for a custom answer, which is
  // saved as the answer itself (so any answer that isn't an option value is an "Other" answer).
  otherPlaceholder?: string;
};

type Props = BaseQuestionProps & {
  question: SurveySelectQuestionProps;
  useNewDesign?: boolean;
  onlyShowInlineAnswerButtons?: boolean; // only applicable if `useNewDesign` is true
};

const isOptionValue = (question: SurveySelectQuestionProps, answer: string | undefined) =>
  question.options.some((o) => o.value === answer);

const selectedValueForAnswer = (
  question: SurveySelectQuestionProps,
  answer: string | undefined,
) => {
  if (!answer) {
    return "";
  }
  return isOptionValue(question, answer) ? answer : SELECT_QUESTION_OTHER_VALUE;
};

/** Returns the label to display for a select question's answer (the custom text for "Other"). */
export const selectAnswerDisplayText = (
  question: SurveySelectQuestionProps,
  answer: string | undefined,
) => question.options.find((o) => o.value === answer)?.label ?? answer;

export const SurveySelectQuestion = ({
  question,
  answer,
  onAnswer,
  disabled,
  loading,
  collapseTopSpacing,
  useNewDesign,
  onlyShowInlineAnswerButtons,
}: Props) => {
  const allowOther = question.otherPlaceholder !== undefined;
  const [selectedValue, setSelectedValue] = useState(selectedValueForAnswer(question, answer));
  const [otherText, setOtherText] = useState(isOptionValue(question, answer) ? "" : answer ?? "");
  const [saving, setSaving] = useState(false);

  // Only resync from the saved answer (not on every `question` change, since survey definitions are
  // rebuilt on each render) so an unsaved "Other" selection isn't reset
  useEffect(() => {
    setSelectedValue(selectedValueForAnswer(question, answer));
    setOtherText(isOptionValue(question, answer) ? "" : answer ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answer]);

  if (question.notPresent) {
    return null;
  }

  const save = async (value: string) => {
    setSaving(true);
    try {
      await onAnswer(value);
    } finally {
      setSaving(false);
    }
  };

  const isOtherSelected = selectedValue === SELECT_QUESTION_OTHER_VALUE;
  const trimmedOtherText = otherText.trim();
  const canSaveOther = trimmedOtherText !== "" && trimmedOtherText !== answer;
  const isBusy = disabled || loading || saving;

  const items = [
    ...question.options,
    ...(allowOther ? [{ value: SELECT_QUESTION_OTHER_VALUE, label: "Other" }] : []),
  ];

  const inputs = (
    <div className="flex flex-col gap-[12px] max-w-[400px]">
      <SelectMenuStandard
        id={`${question.slug}-select`}
        items={items}
        selectedValue={selectedValue}
        placeholder={question.placeholder}
        onSelect={(value) => {
          if (isBusy) return;
          setSelectedValue(value);
          // "Other" is only saved once a custom answer is entered
          if (value !== SELECT_QUESTION_OTHER_VALUE) {
            save(value);
          }
        }}
      />
      {isOtherSelected && (
        <div className="flex items-center gap-[8px]">
          <div className="flex-1">
            <TextInputBase
              field={`${question.slug}-other`}
              value={otherText}
              placeholder={question.otherPlaceholder}
              disabled={isBusy}
              // Focus when switching to "Other", but not when loading a saved "Other" answer
              autoFocus={selectedValueForAnswer(question, answer) !== SELECT_QUESTION_OTHER_VALUE}
              onChange={(e) => setOtherText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSaveOther && !isBusy) {
                  save(trimmedOtherText);
                }
              }}
            />
          </div>
          <Button
            color="primary"
            disabled={!canSaveOther || isBusy}
            onClick={() => save(trimmedOtherText)}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );

  if (useNewDesign) {
    return (
      <div className="tw-boundary">
        {!onlyShowInlineAnswerButtons && (
          <div>
            <div className="text-[30px] font-bold text-neutral-800 text-left leading-[45px]">
              {question.question}
            </div>
            {question.helpText && (
              <div className="mt-[24px] text-[20px] text-neutral-700 text-left">
                {question.helpText}
              </div>
            )}
          </div>
        )}
        <div className={clsx(!onlyShowInlineAnswerButtons && "mx-auto mt-[36px] max-w-[400px]")}>
          {inputs}
        </div>
      </div>
    );
  }

  return (
    <FadeTransition
      key={`question-${question.slug}`}
      cls="survey-question-fade"
      in={!!answer || question.visible}
      unmountOnExit
      appear
    >
      <div
        className={clsx("survey--question", { "survey-question--top-margin": !collapseTopSpacing })}
        ref={question.ref}
      >
        <QuestionContainer>
          <QuestionLabel
            label={question.question}
            helpText={question.helpText}
            header={question.header}
            description={question.description}
          />
          <div className="tw-boundary mt-md">{inputs}</div>
        </QuestionContainer>
      </div>
    </FadeTransition>
  );
};
